// src/components/ContactDialog.jsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography, CircularProgress,
  FormControlLabel, Checkbox, FormHelperText, Snackbar, Alert
} from '@mui/material';
import api from '../api';

const ContactDialog = ({
  open,
  onClose,
  toEmail,
  subject = 'Contact Request - Sunshine Marketplace',
  context = {},
  product
}) => {
  const [values, setValues] = React.useState({ name: '', phone: '', email: '' });
  const [tcAgreed, setTcAgreed] = React.useState(false);
  const [touched, setTouched] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  // existing inline messages (optional to keep)
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  // NEW: snackbar controls
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [errorOpen, setErrorOpen] = React.useState(false);

  const [tcTouched, setTcTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setValues({ name: '', phone: '', email: '' });
      setTouched({});
      setTcAgreed(false);
      setError('');
      setSuccess('');
      setLoading(false);
      setSuccessOpen(false);
      setErrorOpen(false);
      setTcTouched(false);
    }
  }, [open]);

  // --- Validation ---
  const emailValid = React.useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim());
  }, [values.email]);

  const phoneValid = React.useMemo(() => {
    const digits = values.phone.replace(/\D/g, '');
    return digits.length >= 6;
  }, [values.phone]);

  const nameValid = values.name.trim().length >= 2;
  const formValid = nameValid && emailValid && phoneValid && tcAgreed;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues(v => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
  };

  const handleSubmit = async () => {
    if (!formValid) {
      setTouched({ name: true, email: true, phone: true });
      setTcTouched(true);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const apiURL = (window.APP_CONFIG?.API_URL || '') + 'marketplace/contact/send';
      const payload = {
        toEmail,
        subject,
        data: {
          name: values.name.trim(),
          phone: values.phone.trim(),
          email: values.email.trim(),
          id_prod: product?.id_prod,
          desc_prod_full: product?.desc_prod_full,
          ...context,
        }
      };

      const resp = await api.post(apiURL, payload);
      if (resp?.status >= 200 && resp?.status < 300) {
        // Inline text (optional)
        setSuccess('Thanks—we’ve received your details.');

        // Popup
        setSuccessOpen(true);

        // Optional: close dialog after a short delay
        setTimeout(() => onClose?.(), 900);
      } else {
        throw new Error(`Unexpected status ${resp?.status}`);
      }
    } catch (err) {
      console.error('Contact send error:', err);

      // Inline text (optional)
      setError('Sorry, something went wrong sending your details. Please try again.');

      // Popup
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Optional: support pressing Enter to submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          Please Provide Your Details and Accept the T&Cs, and We’ll Get Back to You.
        </DialogTitle>

        <DialogContent dividers onKeyDown={handleKeyDown}>
          <Typography variant="body2" sx={{fontSize: '1.5rem', fontWeight: 'bold', mb: 2}}>
            All products listed in the Sunshine Marketplace are subject to availability. All items are pickup only from the listed Sunshine Mitre10 store.
            Prices shown are clearance prices and valid only while stocks last. Payment is to be made in-store at the point of collection.
            All sales are final. No returns, exchanges, or refunds offered for change of mind or incorrect selection.
          </Typography>
          <Typography variant="body2" sx={{fontSize: '1.5rem', fontWeight: 'bold', mb: 3}}>
            Nothing in these terms excludes or limits rights under Australian Consumer Law. 
            Consumers are entitled to a repair, replacement, or refund for major faults where applicable.
          </Typography>        

          <Stack spacing={2}>
            <TextField
              label="Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!touched.name && !nameValid}
              helperText={touched.name && !nameValid ? 'Please enter your name (min 2 characters).' : ' '}
              autoFocus
              fullWidth
              required
            />

            <TextField
              label="Phone"
              name="phone"
              value={values.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!touched.phone && !phoneValid}
              helperText={touched.phone && !phoneValid ? 'Please enter a valid phone number.' : ' '}
              fullWidth
              required
            />

            <TextField
              label="Email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!touched.email && !emailValid}
              helperText={touched.email && !emailValid ? 'Please enter a valid email address.' : ' '}
              fullWidth
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={tcAgreed}
                  onChange={(e) => setTcAgreed(e.target.checked)}
                  onBlur={() => setTcTouched(true)}
                />
              }
              label={<span>I have read and agree to the Terms &amp; Conditions.</span>}
            />

            {tcTouched && !tcAgreed && (
              <FormHelperText error>
                You must accept the Terms &amp; Conditions to continue.
              </FormHelperText>
            )}

            {/* Inline messages (optional, can remove if redundant with Snackbar) */}
            {error ? (
              <Typography color="error" variant="body2">{error}</Typography>
            ) : null}
            {success ? (
              <Typography color="success.main" variant="body2">{success}</Typography>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formValid || loading}
            endIcon={loading ? <CircularProgress color="inherit" size={18} /> : null}
          >
            {loading ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          // prevent closing on clickaway if you want it to persist longer
          if (reason === 'clickaway') return;
          setSuccessOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessOpen(false)}
          sx={{ width: '100%' }}
        >
          Thanks—we’ve received your details.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setErrorOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorOpen(false)}
          sx={{ width: '100%' }}
        >
          Sorry, something went wrong sending your details. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactDialog;
